import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

// Material
import { MaterialModule } from '@app/material.module';

// Ng-bootstrap
import { NgbActiveModal, NgbModalModule, NgbModule, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { NgCircleProgressModule } from 'ng-circle-progress';

// Services
import { CategoriesService } from '@app/core/services/categories.service';
import { CountriesService } from '@app/core/services/countries.service';
import { ClaimingStaticService } from './services/translations/claiming.static.service';
import { FaqService } from './services/faq.service';

//Components
import { OnboardingComponent } from './components/onboarding/onboarding.component';
import { ModalTermsConditionsComponent } from '@app/core/components/modal/model-term-conditions';
import { GooglePlacesDirective } from './directives/google-places.directive';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NavigationBarComponent } from './components/navigation-bar/nav-bar.component';
import { RouterModule } from '@angular/router';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { ProgressSpinnerComponent } from './components/progress-spinner/progress-spinner.component';
import { ProgressSaveSpinnerComponent } from './components/progress-save-spinner/progress-save-spinner.component';
import { SavebarComponent } from './components/savebar/savebar.component';
import { GenericErrorCardComponent } from './components/generic-error-card/generic-error-card.component';
import { CarouselComponent } from './components/claiming-carousel-component/carousel.component';
import { CarouselSliderComponent } from './components/claiming-carousel-component/carousel-slider/carousel-slider.component';
import { CarouselFooterComponent } from './components/claiming-carousel-component/carousel-footer/carousel-footer.component';
import { ModalBottomsheetComponent } from './components/modal-bottomsheet/modal-bottomsheet.component';
import { ClaimingBottomsheetComponent } from './components/claiming-bottomsheet/claiming-bottomsheet.component';
import { InformativeBarComponent } from '@app/core/components/informative-bar/informative-bar.component';
import { DragDropDirective } from '@app/core/directives/drag-n-drop.directive';
import { FileUploadComponent } from '@app/core/components/file-upload/file-upload.component';
import { OpenHoursComponent } from '@app/core/components/open-hours/open-hours.component';
import { ImageViewerComponent } from './components/image-cropper/image-viewer.component';
import { UploadingCardComponent } from './components/uploading-card/uploading-card.component';
import { OfferingsComponent } from './components/offerings/offerings.component';
import { CollapsableComponent } from './components/collapsable/collapsable.component';
import { RatingComponent } from './components/rating-component/rating.component';
import { StarRatingPipe } from '@app/features/review/pipes/starRating.pipe';
import { GmbStatusProfileComponent } from './components/gmb-status-profile/gmb-status-profile.component';
import { DishNavbarComponent } from './components/dish-navbar/dish-navbar.component';
import { DishFooterComponent } from './components/dish-footer/dish-footer.component';
import { MatCarouselComponent } from './components/carousel/carousel.component';
import { MatCarouselSlideComponent } from './components/carousel/carousel-slide/carousel-slide.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { ClicksOnGoogleComponent } from './components/uberall-widgets/clicks-on-google/clicks-on-google.component';
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { HotTeaLoadingComponent } from './components/hot-tea-loading/hot-tea-loading.component';
import { WeblistingRatingStarsComponent } from './components/weblisting-rating-starts/weblisting-rating-stars.component';
import { DateFromStringPipe } from './pipes/date-from-string.pipe';
import { TimeFromDateStringPipe } from './pipes/time-from-date-string.pipe';
import { MultiTranslateHttpLoader } from './utils/MultiTranslateHttpLoader';

@NgModule({
  exports: [
    OnboardingComponent,
    GooglePlacesDirective,
    ModalTermsConditionsComponent,
    CommonModule,
    NavigationBarComponent,
    ProgressBarComponent,
    ProgressSpinnerComponent,
    MaterialModule,
    RouterModule,
    TranslateModule,
    SavebarComponent,
    ProgressSaveSpinnerComponent,
    GenericErrorCardComponent,
    CarouselComponent,
    ClaimingBottomsheetComponent,
    InformativeBarComponent,
    FileUploadComponent,
    OpenHoursComponent,
    ImageViewerComponent,
    UploadingCardComponent,
    OfferingsComponent,
    RatingComponent,
    StarRatingPipe,
    GmbStatusProfileComponent,
    CollapsableComponent,
    DishNavbarComponent,
    DishFooterComponent,
    MatCarouselComponent,
    MatCarouselSlideComponent,
    ClicksOnGoogleComponent,
    DateAgoPipe,
    DateFromStringPipe,
    TimeFromDateStringPipe,
    HotTeaLoadingComponent,
    WeblistingRatingStarsComponent,
  ],
  declarations: [
    OnboardingComponent,
    ModalTermsConditionsComponent,
    NavigationBarComponent,
    ProgressBarComponent,
    ProgressSpinnerComponent,
    SavebarComponent,
    ProgressSaveSpinnerComponent,
    GenericErrorCardComponent,
    CarouselComponent,
    CarouselFooterComponent,
    CarouselSliderComponent,
    ModalBottomsheetComponent,
    ClaimingBottomsheetComponent,
    InformativeBarComponent,
    DragDropDirective,
    FileUploadComponent,
    OpenHoursComponent,
    ImageViewerComponent,
    UploadingCardComponent,
    OfferingsComponent,
    CollapsableComponent,
    RatingComponent,
    StarRatingPipe,
    GmbStatusProfileComponent,
    DishNavbarComponent,
    DishFooterComponent,
    MatCarouselComponent,
    MatCarouselSlideComponent,
    TooltipComponent,
    ClicksOnGoogleComponent,
    DateAgoPipe,
    DateFromStringPipe,
    TimeFromDateStringPipe,
    HotTeaLoadingComponent,
    WeblistingRatingStarsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    GooglePlacesDirective,
    NgxEchartsModule.forRoot({
      echarts,
    }),
    MaterialModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    RouterModule,
    NgbPopoverModule,
    NgbModalModule,
    NgbModule,
    NgCircleProgressModule.forRoot({
      radius: 98,
      outerStrokeWidth: 8,
      innerStrokeWidth: 8,
      innerStrokeColor: '#f4f4f4',
      outerStrokeColor: '#ffb607',
      outerStrokeLinecap: 'butt',
      space: -8,
      animation: false,
      animationDuration: 0,
      showSubtitle: false,
      showTitle: false,
      showUnits: false,
    }),
    TranslateModule.forChild({
      loader: { provide: TranslateLoader, useFactory: createTranslateLoader, deps: [HttpClient] },
      isolate: true,
    }),
  ],
  providers: [
    CategoriesService,
    CountriesService,
    NgbActiveModal,
    ClaimingStaticService,
    FaqService,
    DateAgoPipe,
    DateFromStringPipe,
    TimeFromDateStringPipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class CoreModule {}

export function createTranslateLoader(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: `/assets/i18n/core/`, suffix: `.json?v=${Date.now()}` },
    { prefix: `/assets/i18n/dynamic/`, suffix: `.json?v=${Date.now()}` },
  ]);
}
